import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Send, Phone, Mail, ShieldAlert, LifeBuoy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import api from '@/lib/api/axios'
import { getCollectedErrors, clearCollectedErrors } from '@/components/ErrorNotificationProvider'

interface SupportInfo {
    id: number
    description: string
    value: string
}

interface SupportDropdownProps {
    open: boolean
    onClose: () => void
}

export function SupportDropdown({ open, onClose }: SupportDropdownProps) {
    const { t } = useTranslation()
    const ref = useRef<HTMLDivElement>(null)
    const [supportInfo, setSupportInfo] = useState<SupportInfo[]>([])
    const [message, setMessage] = useState('')
    const [sending, setSending] = useState(false)
    const [reference, setReference] = useState<string | null>(null)
    const [showDiagnostic, setShowDiagnostic] = useState(false)

    useEffect(() => {
        if (open) {
            api.get('/api/diagnostics/support-info')
                .then(res => {
                    const data = (res.data as any)?.data || res.data
                    if (Array.isArray(data)) setSupportInfo(data)
                })
                .catch(() => {})
        } else {
            setShowDiagnostic(false)
            setReference(null)
            setMessage('')
        }
    }, [open])

    // Close on click outside
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose()
            }
        }
        setTimeout(() => document.addEventListener('mousedown', handler), 0)
        return () => document.removeEventListener('mousedown', handler)
    }, [open, onClose])

    const handleSend = useCallback(async () => {
        setSending(true)
        try {
            const errors = getCollectedErrors()
            const res = await api.post('/api/diagnostics/send-report', {
                errors,
                appState: {
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    screenSize: `${window.innerWidth}x${window.innerHeight}`,
                    language: navigator.language,
                    timestamp: new Date().toISOString(),
                },
                userMessage: message.trim(),
            })
            const data = (res.data as any)?.data || res.data
            setReference(data.reference || 'VS-UNKNOWN')
            clearCollectedErrors()
        } catch {
            setReference('VS-ERROR')
        } finally {
            setSending(false)
        }
    }, [message])

    if (!open) return null

    const phoneInfo = supportInfo.find(s => s.description.toLowerCase().includes('phone') && !s.description.toLowerCase().includes('goteam'))
    const emailInfo = supportInfo.find(s => s.description.toLowerCase().includes('email'))
    const goTeamPhone = supportInfo.find(s => s.description === 'GoTeam')
    const goTeamText = supportInfo.find(s => s.description === 'GoTeamText')
    const errorCount = getCollectedErrors().length

    return (
        <div
            ref={ref}
            className="absolute top-[calc(var(--topbar-height)-4px)] left-4 z-[250] w-80 rounded-lg border bg-popover shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-150"
        >
            <div className="p-4 space-y-3">
                {/* Support contacts */}
                {phoneInfo && (
                    <div className="flex items-center gap-3 text-sm">
                        <div className="h-8 w-8 rounded-full bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center flex-shrink-0">
                            <Phone className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">{phoneInfo.description}</p>
                            <a href={`tel:${phoneInfo.value.replace(/\s/g, '')}`} className="font-medium hover:text-primary">
                                {phoneInfo.value}
                            </a>
                        </div>
                    </div>
                )}
                {emailInfo && (
                    <div className="flex items-center gap-3 text-sm">
                        <div className="h-8 w-8 rounded-full bg-green-50 dark:bg-green-950/30 flex items-center justify-center flex-shrink-0">
                            <Mail className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">{emailInfo.description}</p>
                            <a href={`mailto:${emailInfo.value}`} className="font-medium hover:text-primary">
                                {emailInfo.value}
                            </a>
                        </div>
                    </div>
                )}

                {/* GoTeam */}
                {goTeamPhone && (
                    <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 p-2.5 space-y-1.5">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="h-4 w-4 text-red-600 flex-shrink-0" />
                            <span className="text-xs font-semibold text-red-700 dark:text-red-400">GoTeam</span>
                            <a href={`tel:${goTeamPhone.value.replace(/\s/g, '')}`} className="text-xs font-medium text-red-700 dark:text-red-400 hover:underline ml-auto">
                                {goTeamPhone.value}
                            </a>
                        </div>
                        {goTeamText && (
                            <p className="text-[11px] text-red-600 dark:text-red-400 leading-tight">
                                {goTeamText.value}
                            </p>
                        )}
                    </div>
                )}

                {/* Diagnostic report */}
                <div className="border-t pt-3">
                    {reference ? (
                        <div className="flex items-center gap-2 text-green-600 text-sm">
                            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                            <div>
                                <p className="font-medium">Report Sent</p>
                                <p className="text-xs text-muted-foreground">
                                    Ref: <span className="font-mono text-foreground">{reference}</span>
                                </p>
                            </div>
                        </div>
                    ) : !showDiagnostic ? (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full gap-2 text-xs"
                            onClick={() => setShowDiagnostic(true)}
                        >
                            <LifeBuoy className="h-3.5 w-3.5" />
                            {t('diagnostics.sendButton', 'Send Diagnostic Report')}
                            {errorCount > 0 && (
                                <span className="ml-1 px-1.5 py-0 text-[10px] rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                                    {errorCount}
                                </span>
                            )}
                        </Button>
                    ) : (
                        <div className="space-y-2">
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder={t('diagnostics.messagePlaceholder', 'What were you doing when the problem occurred?')}
                                rows={2}
                                className="resize-none text-xs"
                            />
                            <div className="flex gap-2 justify-end">
                                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowDiagnostic(false)}>
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button size="sm" className="text-xs h-7" onClick={handleSend} disabled={sending}>
                                    {sending ? (
                                        <svg className="animate-spin h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                                    ) : (
                                        <Send className="h-3.5 w-3.5 mr-1" />
                                    )}
                                    {t('diagnostics.send', 'Send')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
